import { useEffect, useState, useCallback } from "react";
import {
  Sprout,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  IndianRupee,
  Package,
  BarChart3,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { showError } from "../../../utils/toastUtils";
import api from "../../../configs/api";

const fmt = (n) =>
  Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-borderLight bg-surfaceColor p-4 flex items-start gap-3">
    <div className="w-9 h-9 flex items-center justify-center bg-primaryAccent/10 text-primaryAccent shrink-0">
      <Icon size={18} />
    </div>
    <div>
      <p className="text-xs text-descriptionText mb-0.5">{label}</p>
      <p className="text-xl font-bold text-headingText leading-none">{value}</p>
    </div>
  </div>
);

const Breadcrumb = ({ plant, grade }) => (
  <div className="flex items-center gap-1.5 text-xs text-descriptionText mb-4">
    <span className={plant && !grade ? "text-headingText font-medium" : ""}>
      All Plants
    </span>
    {plant && (
      <>
        <ChevronRight size={12} className="opacity-40" />
        <span className={grade ? "" : "text-headingText font-medium"}>
          {plant}
        </span>
      </>
    )}
    {grade && (
      <>
        <ChevronRight size={12} className="opacity-40" />
        <span className="text-headingText font-medium">{grade}</span>
      </>
    )}
  </div>
);

const BackButton = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-xs text-descriptionText hover:text-headingText border border-borderLight px-3 py-1.5 mb-4 transition hover:bg-surfaceColor"
  >
    <ChevronLeft size={13} />
    {label}
  </button>
);

const PlantListView = ({
  grouped,
  history,
  onSelectPlant,
  onRefresh,
  loading,
}) => {
  const plants = Object.keys(grouped);
  const totalCost = history.reduce((s, c) => s + (c.totalCost ?? 0), 0);

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard icon={Sprout} label="Total Plants" value={plants.length} />
        <StatCard
          icon={FlaskConical}
          label="Calculations"
          value={history.length}
        />
        <StatCard
          icon={BarChart3}
          label="Combined Cost"
          value={`₹ ${fmt(totalCost)}`}
        />
      </div>

      {/* Header row */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-descriptionText uppercase tracking-wider">
          Select a plant
        </p>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-descriptionText hover:text-headingText transition disabled:opacity-40"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Empty */}
      {plants.length === 0 && (
        <div className="border border-dashed border-borderLight p-16 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4 mx-auto">
            <AlertCircle size={28} />
          </div>
          <p className="text-lg font-semibold text-headingText">
            No calculations found
          </p>
          <p className="text-sm text-descriptionText mt-1">
            Save a calculation from the Calculator tab to see it here.
          </p>
        </div>
      )}

      {/* Plant cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {plants.map((plant) => {
          const grades = grouped[plant];
          const gradeNames = Object.keys(grades);
          const allCalcs = Object.values(grades).flat();
          const total = allCalcs.reduce((s, c) => s + (c.totalCost ?? 0), 0);

          return (
            <button
              key={plant}
              onClick={() => onSelectPlant(plant)}
              className="border border-borderLight bg-white hover:bg-surfaceColor hover:border-primaryAccent/40 transition text-left p-4 flex flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <Sprout size={15} className="text-primaryAccent shrink-0" />
                <span className="text-sm font-semibold text-headingText">
                  {plant}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-descriptionText flex items-center gap-1">
                  <FlaskConical size={11} />
                  {gradeNames.length} grade{gradeNames.length !== 1 ? "s" : ""}
                  &nbsp;·&nbsp;
                  <Package size={11} />
                  {allCalcs.length} calc{allCalcs.length !== 1 ? "s" : ""}
                </span>
                <span className="flex items-center gap-0.5 text-sm font-bold text-primaryAccent">
                  <IndianRupee size={11} />
                  {fmt(total)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const GradeListView = ({ plant, grades, onSelectGrade, onBack }) => {
  const gradeNames = Object.keys(grades);
  const allCalcs = Object.values(grades).flat();
  const plantTotal = allCalcs.reduce((s, c) => s + (c.totalCost ?? 0), 0);

  return (
    <div className="space-y-5">
      <BackButton label="All Plants" onClick={onBack} />
      <Breadcrumb plant={plant} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard
          icon={FlaskConical}
          label="Grade Mixtures"
          value={gradeNames.length}
        />
        <StatCard icon={Package} label="Calculations" value={allCalcs.length} />
        <StatCard
          icon={BarChart3}
          label="Plant Total"
          value={`₹ ${fmt(plantTotal)}`}
        />
      </div>

      <p className="text-xs font-semibold text-descriptionText uppercase tracking-wider">
        Select a grade mixture
      </p>

      <div className="space-y-2">
        {gradeNames.map((grade) => {
          const calcs = grades[grade];
          const total = calcs.reduce((s, c) => s + (c.totalCost ?? 0), 0);

          return (
            <button
              key={grade}
              onClick={() => onSelectGrade(grade)}
              className="w-full border border-borderLight bg-white hover:bg-surfaceColor hover:border-primaryAccent/40 transition text-left flex items-center gap-3 px-4 py-3"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-primaryAccent/10 text-primaryAccent shrink-0">
                <FlaskConical size={15} />
              </div>
              <span className="flex-1 text-sm font-medium text-headingText">
                {grade}
              </span>
              <span className="text-xs text-descriptionText mr-3">
                {calcs.length} calc{calcs.length !== 1 ? "s" : ""}
              </span>
              <span className="flex items-center gap-0.5 text-sm font-bold text-primaryAccent mr-2">
                <IndianRupee size={11} />
                {fmt(total)}
              </span>
              <ChevronRight
                size={14}
                className="text-descriptionText opacity-50 shrink-0"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DetailView = ({ plant, grade, calcs, onBack }) => {
  const gradeTotal = calcs.reduce((s, c) => s + (c.totalCost ?? 0), 0);

  return (
    <div className="space-y-5">
      <BackButton label={plant} onClick={onBack} />
      <Breadcrumb plant={plant} grade={grade} />

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={FlaskConical}
          label="Calculations"
          value={calcs.length}
        />
        <StatCard
          icon={BarChart3}
          label="Grade Total"
          value={`₹ ${fmt(gradeTotal)}`}
        />
      </div>

      <div className="space-y-4">
        {calcs.map((calc) => (
          <div key={calc._id} className="border border-borderLight bg-white">
            {/* Card header */}
            <div className="flex items-center gap-3 bg-surfaceColor px-4 py-3 border-b border-borderLight">
              <FlaskConical size={14} className="text-primaryAccent shrink-0" />
              <span className="flex-1 text-sm font-semibold text-headingText">
                {calc.gradeMixture?.gradeName ?? "—"}
              </span>
              <span className="text-[11px] text-descriptionText">
                {new Date(calc.createdAt).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
              <span className="flex items-center gap-0.5 text-sm font-bold text-primaryAccent ml-3">
                <IndianRupee size={11} />
                {fmt(calc.totalCost)}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] font-semibold text-descriptionText uppercase tracking-wide border-b border-borderLight">
                    <th className="text-left px-4 py-2">Product</th>
                    <th className="text-right px-4 py-2">Qty</th>
                    <th className="text-right px-4 py-2">Price / Unit</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borderLight">
                  {calc.products?.map((p, i) => (
                    <tr key={i} className="hover:bg-surfaceColor transition">
                      <td className="px-4 py-2.5 font-medium text-headingText">
                        {p.snapshotName ?? p.product?.name ?? "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right text-descriptionText">
                        {p.quantity} {p.snapshotUnit ?? p.product?.unit ?? "kg"}
                      </td>
                      <td className="px-4 py-2.5 text-right text-descriptionText">
                        ₹ {fmt(p.snapshotPrice ?? 0)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-headingText">
                        ₹ {fmt((p.quantity ?? 0) * (p.snapshotPrice ?? 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-borderLight">
                    <td
                      colSpan={3}
                      className="px-4 pt-2 pb-3 text-right text-xs font-semibold text-descriptionText uppercase tracking-wide"
                    >
                      Grand Total
                    </td>
                    <td className="px-4 pt-2 pb-3 text-right font-bold text-primaryAccent">
                      ₹ {fmt(calc.totalCost)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AllGradeCalculatorTab = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/calculator/history");
      setHistory(res.data.data ?? []);
    } catch {
      showError("Failed to load calculations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const grouped = {};
  history.forEach((calc) => {
    const plantName = calc.plant?.plantName ?? "Unknown Plant";
    const gradeName = calc.gradeMixture?.gradeName ?? "—";
    if (!grouped[plantName]) grouped[plantName] = {};
    if (!grouped[plantName][gradeName]) grouped[plantName][gradeName] = [];
    grouped[plantName][gradeName].push(calc);
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 border border-borderLight bg-surfaceColor animate-pulse"
          />
        ))}
      </div>
    );
  }

  const currentGrades = selectedPlant ? (grouped[selectedPlant] ?? {}) : {};
  const currentCalcs =
    selectedPlant && selectedGrade ? (currentGrades[selectedGrade] ?? []) : [];

  return (
    <div className="max-w-7xl mx-auto">
      {!selectedPlant && (
        <PlantListView
          grouped={grouped}
          history={history}
          onSelectPlant={(p) => {
            setSelectedPlant(p);
            setSelectedGrade(null);
          }}
          onRefresh={fetchHistory}
          loading={loading}
        />
      )}

      {/* VIEW 2 — Grades for a plant */}
      {selectedPlant && !selectedGrade && (
        <GradeListView
          plant={selectedPlant}
          grades={currentGrades}
          onSelectGrade={(g) => setSelectedGrade(g)}
          onBack={() => {
            setSelectedPlant(null);
            setSelectedGrade(null);
          }}
        />
      )}

      {/* VIEW 3 — Detail for a grade */}
      {selectedPlant && selectedGrade && (
        <DetailView
          plant={selectedPlant}
          grade={selectedGrade}
          calcs={currentCalcs}
          onBack={() => setSelectedGrade(null)}
        />
      )}
    </div>
  );
};

export default AllGradeCalculatorTab;
